import { theme as antdTheme, type ThemeConfig } from "antd";
import { accent, dark, fontFamily, fontFamilyMono, light, radius, status } from "./tokens";

/** Widen the literal token values so both palettes satisfy the same shape. */
type Palette = { readonly [K in keyof typeof light]: string };

function buildTheme(palette: Palette, mode: "light" | "dark"): ThemeConfig {
	const isDark = mode === "dark";

	return {
		algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
		token: {
			colorPrimary: palette.accent,
			colorInfo: palette.accent,
			colorSuccess: status.success,
			colorWarning: status.warning,
			colorError: status.error,

			colorBgLayout: palette.canvas,
			colorBgContainer: palette.surface,
			colorBgElevated: palette.surface,

			colorBorder: palette.border,
			colorBorderSecondary: palette.borderSubtle,

			colorText: palette.text,
			colorTextSecondary: palette.textSecondary,
			colorTextTertiary: palette.textMuted,
			colorTextQuaternary: palette.textMuted,

			borderRadius: radius.base,
			borderRadiusLG: radius.lg,
			borderRadiusSM: radius.sm,

			fontFamily,
			fontFamilyCode: fontFamilyMono,
			fontSize: 14,

			controlHeight: 36,
			controlHeightLG: 42,
			controlHeightSM: 28,

			lineWidth: 1,
			wireframe: false,

			// Hairline elevation instead of the default soft drop shadows.
			boxShadow: isDark
				? "0 1px 2px rgba(0,0,0,.6), 0 8px 24px rgba(0,0,0,.45)"
				: "0 1px 2px rgba(24,24,27,.04), 0 8px 24px rgba(24,24,27,.06)",
			boxShadowSecondary: isDark
				? "0 1px 2px rgba(0,0,0,.5), 0 6px 16px rgba(0,0,0,.4)"
				: "0 1px 2px rgba(24,24,27,.04), 0 6px 16px rgba(24,24,27,.05)",
			boxShadowTertiary: "none",
		},
		components: {
			Layout: {
				siderBg: palette.chrome,
				headerBg: palette.chrome,
				bodyBg: palette.canvas,
				headerHeight: 56,
				headerPadding: "0 20px",
			},
			Menu: {
				itemBg: "transparent",
				subMenuItemBg: "transparent",
				itemBorderRadius: radius.sm,
				itemHeight: 36,
				itemMarginInline: 8,
				itemMarginBlock: 2,
				itemPaddingInline: 10,
				iconSize: 16,
				collapsedIconSize: 18,
				itemColor: palette.textSecondary,
				itemHoverColor: palette.text,
				itemHoverBg: palette.hover,
				itemSelectedColor: palette.accent,
				itemSelectedBg: palette.accentSoft,
				activeBarWidth: 0,
			},
			Button: {
				fontWeight: 500,
				primaryShadow: "none",
				defaultShadow: "none",
				dangerShadow: "none",
				paddingInline: 14,
			},
			Card: {
				paddingLG: 20,
				headerFontSize: 14,
				headerHeight: 48,
			},
			Table: {
				headerBg: palette.surface,
				headerColor: palette.textMuted,
				headerSplitColor: "transparent",
				borderColor: palette.borderSubtle,
				rowHoverBg: palette.hover,
				cellPaddingBlock: 12,
				cellPaddingInline: 16,
				footerBg: "transparent",
			},
			Input: {
				paddingInline: 12,
				activeShadow: `0 0 0 3px ${isDark ? accent.softDark : accent.soft}`,
			},
			InputNumber: {
				activeShadow: `0 0 0 3px ${isDark ? accent.softDark : accent.soft}`,
			},
			Select: {
				optionSelectedBg: palette.accentSoft,
				optionSelectedColor: palette.accent,
			},
			Modal: {
				titleFontSize: 16,
				headerBg: palette.surface,
				contentBg: palette.surface,
				borderRadiusLG: radius.lg,
			},
			Tag: {
				borderRadiusSM: radius.sm,
				defaultBg: palette.hover,
				defaultColor: palette.textSecondary,
			},
			Statistic: {
				contentFontSize: 28,
				titleFontSize: 13,
			},
			Tooltip: {
				colorBgSpotlight: isDark ? "#26262a" : "#18181b",
			},
			Segmented: {
				itemSelectedBg: palette.surface,
				trackBg: palette.hover,
			},
			Dropdown: {
				borderRadiusLG: radius.base,
			},
			Popconfirm: {
				borderRadiusLG: radius.base,
			},
			Form: {
				labelColor: palette.textSecondary,
				labelFontSize: 13,
				verticalLabelPadding: "0 0 6px",
				itemMarginBottom: 18,
			},
			Pagination: {
				itemActiveBg: palette.accentSoft,
			},
			Avatar: {
				containerSize: 30,
			},
		},
	};
}

export const lightTheme = buildTheme(light, "light");
export const darkTheme = buildTheme(dark, "dark");

export const palettes = { light, dark } as const;
